import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import {
  ArrowBackOutlined,
  CalendarMonthOutlined,
  DownloadOutlined,
  LocationOnOutlined,
  PreviewOutlined,
  ScheduleOutlined,
  StarBorderRounded,
  StarRounded,
} from "@mui/icons-material";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import jsPDF from "jspdf";

import SearchSelect, { OptionItem } from "components/atoms/SearchSelect/SearchSelect";
import {
  emitParticipantCertificate,
  findAvailableParticipantCertificateDetail,
  submitParticipantSatisfactionSurvey,
} from "../../../API/apiParticipantCertificates";
import {
  findSatisfactionSurveyQuestionOptions,
  findSatisfactionSurveyTemplate,
} from "../../../API/apiSatisfactionSurveyTemplates";
import { SatisfactionSurveyQuestion, SatisfactionSurveyTemplate } from "../../../types/SatisfactionSurveyTemplate";
import {
  AvailableParticipantCertificateDetail,
  ParticipantCertificateEmissionData,
  SatisfactionSurveyAnswerPayload,
} from "../../../types/ParticipantCertificate";
import { formatarDataDbParaBr, formatarDatetimeToDataBr } from "../../../utils/Masks";
import { montarEnderecoTurma } from "../../../utils/turmaEndereco";
import {
  buildCertificateVariableMap,
  getPdfPageSize,
  hydrateCertificatePage,
  renderCertificatePageToPdf,
  replaceCertificateVariables,
  replaceCertificateVariablesInHtml,
} from "../../../utils/certificateEmission";
import { registerCertificatePdfFonts } from "../../../utils/certificatePdfFonts";
import { CertificatePage } from "../../../types/CertificateTemplate";
import styles from "./Certificados.module.css";
import stylesInput from "../../../styles/Input.module.css";
import stylesButton from "../../../styles/Button.module.css";

type QuestionAnswerValue = string | number | Array<string | number>;
type QuestionOptionsMap = Record<number, Array<{ value: string | number; label: string }>>;

const escalaOptions = [
  { value: 1, label: "Péssimo" },
  { value: 2, label: "Ruim" },
  { value: 3, label: "Regular" },
  { value: 4, label: "Bom" },
  { value: 5, label: "Ótimo" },
];

const inferDocumentTypeLabel = (detail: AvailableParticipantCertificateDetail | null) => {
  if (!detail) return "Certificado";
  if (detail.certificate_template_document_type_label) {
    return detail.certificate_template_document_type_label;
  }

  const normalizedName = detail.certificate_template_name?.toLowerCase() || "";
  return normalizedName.includes("declara") ? "Declaração" : "Certificado";
};

const EmitirCertificado = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const turmaId = searchParams.get("turma");

  const [detail, setDetail] = useState<AvailableParticipantCertificateDetail | null>(null);
  const [surveyTemplate, setSurveyTemplate] = useState<SatisfactionSurveyTemplate | null>(null);
  const [surveyAnswers, setSurveyAnswers] = useState<Record<number, QuestionAnswerValue>>({});
  const [surveyOptions, setSurveyOptions] = useState<QuestionOptionsMap>({});
  const [emissionData, setEmissionData] = useState<ParticipantCertificateEmissionData | null>(null);
  const [previewPages, setPreviewPages] = useState<{ front: CertificatePage; back: CertificatePage | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSurvey, setLoadingSurvey] = useState(false);
  const [loadingEmit, setLoadingEmit] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);

  const loadDetail = async (currentTurmaId: string) => {
    const result = await findAvailableParticipantCertificateDetail(currentTurmaId);
    setDetail(result);
    return result;
  };

  useEffect(() => {
    const loadPage = async () => {
      if (!turmaId) {
        setMsgError("Turma nao informada para emissao do certificado.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setMsgError(null);
        const detailData = await loadDetail(turmaId);

        if (detailData.pesquisa?.configurada && detailData.pesquisa.satisfaction_survey_template_id) {
          const template = await findSatisfactionSurveyTemplate(
            detailData.pesquisa.satisfaction_survey_template_id,
          );
          setSurveyTemplate(template);

          if (template) {
            const tableQuestions = template.questions.filter(
              (question) => question.active && question.tipo === "tabela",
            );

            if (tableQuestions.length > 0) {
              const optionsEntries = await Promise.all(
                tableQuestions.map(async (question) => {
                  try {
                    const options = await findSatisfactionSurveyQuestionOptions(template.id, question.id || 0);
                    return [question.id || 0, options] as const;
                  } catch {
                    return [question.id || 0, []] as const;
                  }
                }),
              );

              setSurveyOptions(Object.fromEntries(optionsEntries));
            }
          }
        }
      } catch (error: any) {
        setMsgError(error?.message || "Nao foi possivel carregar os dados do documento.");
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [turmaId]);

  const addressLabel = useMemo(() => {
    if (!detail) return "";
    return montarEnderecoTurma(detail);
  }, [detail]);

  const activeSurveyQuestions = useMemo(() => {
    return (surveyTemplate?.questions || [])
      .filter((question) => question.active)
      .sort((a, b) => a.ordem - b.ordem);
  }, [surveyTemplate]);

  const surveyIsBlocking = Boolean(
    detail?.pesquisa?.configurada &&
      detail.pesquisa.obrigatoria_para_certificado &&
      detail.pesquisa.pendente,
  );

  const surveyIsOptionalPending = Boolean(
    detail?.pesquisa?.configurada &&
      !detail.pesquisa.obrigatoria_para_certificado &&
      detail.pesquisa.pendente,
  );

  const handleCheckboxToggle = (questionId: number, optionValue: string | number, checked: boolean) => {
    setSurveyAnswers((current) => {
      const currentValue = Array.isArray(current[questionId]) ? [...(current[questionId] as Array<string | number>)] : [];
      const nextValue = checked
        ? Array.from(new Set([...currentValue, optionValue]))
        : currentValue.filter((value) => value !== optionValue);

      return {
        ...current,
        [questionId]: nextValue,
      };
    });
  };

  const validateSurvey = () => {
    for (const question of activeSurveyQuestions) {
      if (!question.obrigatorio) continue;

      const answer = surveyAnswers[question.id || 0];

      if (question.tipo === "checkbox") {
        if (!Array.isArray(answer) || answer.length === 0) {
          setMsgError(`A pergunta obrigatoria "${question.nome}" precisa ser respondida.`);
          return false;
        }
        continue;
      }

      if (question.tipo === "texto_livre") {
        if (!String(answer || "").trim()) {
          setMsgError(`A pergunta obrigatoria "${question.nome}" precisa ser respondida.`);
          return false;
        }
        continue;
      }

      if (answer === undefined || answer === null || answer === "") {
        setMsgError(`A pergunta obrigatoria "${question.nome}" precisa ser respondida.`);
        return false;
      }
    }

    return true;
  };

  const handleSubmitSurvey = async () => {
    if (!turmaId || !surveyTemplate) {
      return;
    }

    if (!validateSurvey()) {
      return;
    }

    const respostas: SatisfactionSurveyAnswerPayload[] = activeSurveyQuestions
      .map((question) => ({
        pergunta_id: question.id || 0,
        resposta: surveyAnswers[question.id || 0],
      }))
      .filter((item) => item.pergunta_id && item.resposta !== undefined);

    try {
      setLoadingSurvey(true);
      setMsgError(null);
      await submitParticipantSatisfactionSurvey(turmaId, respostas);
      await loadDetail(turmaId);
      setMsgSuccess("Pesquisa de satisfação enviada com sucesso.");
    } catch (error: any) {
      setMsgError(error?.message || "Nao foi possivel enviar a pesquisa de satisfação.");
    } finally {
      setLoadingSurvey(false);
    }
  };

  const hydratePreviewPages = async (emission: ParticipantCertificateEmissionData) => {
    const variableMap = buildCertificateVariableMap(
      emission.dados,
      emission.codigo,
      formatarDatetimeToDataBr(emission.impresso_em),
    );

    const front = await hydrateCertificatePage(emission.certificate_template.pages.front, variableMap);
    const back = emission.certificate_template.pages.back
      ? await hydrateCertificatePage(emission.certificate_template.pages.back, variableMap)
      : null;

    return { front, back };
  };

  const handleEmitCertificate = async () => {
    if (!turmaId) {
      setMsgError("Turma nao informada.");
      return;
    }

    if (surveyIsBlocking) {
      setMsgError("Preencha a pesquisa obrigatoria antes de emitir o documento.");
      return;
    }

    try {
      setLoadingEmit(true);
      setMsgError(null);
      const emission = await emitParticipantCertificate(turmaId);
      const hydratedPages = await hydratePreviewPages(emission);

      setEmissionData(emission);
      setPreviewPages(hydratedPages);
      setMsgSuccess(emission.reimpressao ? "Documento reemitido para visualizacao." : "Documento emitido com sucesso.");
      await loadDetail(turmaId);
    } catch (error: any) {
      setMsgError(error?.message || "Nao foi possivel emitir o documento.");
    } finally {
      setLoadingEmit(false);
    }
  };

  const handleDownloadPdf = () => {
    const runDownload = async () => {
      if (!turmaId) {
        setMsgError("Turma nao informada.");
        return;
      }

      if (surveyIsBlocking) {
        setMsgError("Preencha a pesquisa obrigatoria antes de emitir o documento.");
        return;
      }

      try {
        setLoadingDownload(true);
        setMsgError(null);

        let currentEmission = emissionData;
        let currentPreviewPages = previewPages;

        if (!currentEmission) {
          currentEmission = await emitParticipantCertificate(turmaId);
          setEmissionData(currentEmission);
          await loadDetail(turmaId);
        }

        if (!currentPreviewPages && currentEmission) {
          currentPreviewPages = await hydratePreviewPages(currentEmission);
          setPreviewPages(currentPreviewPages);
        }

        if (!currentEmission || !currentPreviewPages) {
          setMsgError("Nao foi possivel preparar o PDF do documento.");
          return;
        }

        const orientation = currentEmission.certificate_template.page.orientation;
        const { width, height } = getPdfPageSize(orientation);
        const doc = new jsPDF({
          orientation,
          unit: "px",
          format: [width, height],
          compress: true,
        });
        await registerCertificatePdfFonts(doc);

        const variableMap = buildCertificateVariableMap(
          currentEmission.dados,
          currentEmission.codigo,
          formatarDatetimeToDataBr(currentEmission.impresso_em),
        );

        renderCertificatePageToPdf(doc, currentPreviewPages.front, orientation, variableMap);

        if (currentPreviewPages.back?.enabled) {
          doc.addPage([width, height], orientation);
          renderCertificatePageToPdf(doc, currentPreviewPages.back, orientation, variableMap);
        }

        const filename = `${(detail?.turma_nome || "certificado").replace(/[\\/:*?"<>|]/g, "-")}.pdf`;
        doc.save(filename);
      } catch (error: any) {
        setMsgError(error?.message || "Nao foi possivel baixar o documento.");
      } finally {
        setLoadingDownload(false);
      }
    };

    void runDownload();
  };

  const renderSelectQuestion = (
    question: SatisfactionSurveyQuestion,
    options: Array<{ value: string | number; label: string }>,
  ) => {
    const selectOptions: OptionItem[] = options.map((option, index) => ({
      id: index + 1,
      nome: option.label,
    }));
    const currentAnswer = surveyAnswers[question.id || 0];
    const selectedOptionId = options.findIndex((option) => option.value === currentAnswer);

    return (
      <SearchSelect
        label=""
        options={selectOptions}
        value={selectedOptionId >= 0 ? selectedOptionId + 1 : null}
        onChange={(id) => {
          const selectedValue = typeof id === "number" ? options[id - 1]?.value : "";
          setSurveyAnswers((current) => ({
            ...current,
            [question.id || 0]: selectedValue ?? "",
          }));
        }}
        paperClassname={stylesInput.paper}
        popperClassname={stylesInput.popper}
        labelClassname={stylesInput.labelSelect}
        inputClassname={stylesInput.inputSelect}
      />
    );
  };

  const renderQuestionField = (question: SatisfactionSurveyQuestion) => {
    const questionId = question.id || 0;
    const answer = surveyAnswers[questionId];

    if (question.tipo === "texto_livre") {
      return (
        <TextField
          size="small"
          variant="standard"
          fullWidth
          multiline
          minRows={3}
          value={typeof answer === "string" ? answer : ""}
          onChange={(event) =>
            setSurveyAnswers((current) => ({
              ...current,
              [questionId]: event.target.value,
            }))
          }
          InputProps={{
            className: stylesInput.input,
            disableUnderline: true,
          }}
        />
      );
    }

    if (question.tipo === "escala") {
      return (
        <RadioGroup
          value={answer ?? ""}
          onChange={(event) =>
            setSurveyAnswers((current) => ({
              ...current,
              [questionId]: Number(event.target.value),
            }))
          }
          className={styles.scaleOptions}
        >
          {escalaOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={
                <span className={styles.scaleRadioLabel}>
                  {option.value} - {option.label}
                </span>
              }
            />
          ))}
        </RadioGroup>
      );
    }

    if (question.tipo === "escala_dez") {
      return (
        <div className={styles.starScale}>
          {Array.from({ length: 10 }, (_, index) => {
            const value = index + 1;
            const isActive = answer === value;

            return (
              <button
                key={value}
                type="button"
                className={`${styles.starButton} ${isActive ? styles.starButtonActive : ""}`}
                onClick={() =>
                  setSurveyAnswers((current) => ({
                    ...current,
                    [questionId]: value,
                  }))
                }
              >
                <span className={styles.starButtonNumber}>{value}</span>
                {isActive ? <StarRounded /> : <StarBorderRounded />}
              </button>
            );
          })}
        </div>
      );
    }

    if (question.tipo === "checkbox") {
      const answerValues = Array.isArray(answer) ? answer : [];
      return (
        <div className={styles.checkboxGroup}>
          {(question.opcoes || []).map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={answerValues.includes(option.value)}
                  onChange={(event) =>
                    handleCheckboxToggle(questionId, option.value, event.target.checked)
                  }
                />
              }
              label={option.label}
            />
          ))}
        </div>
      );
    }

    if (question.tipo === "radio") {
      return (
        <RadioGroup
          value={answer ?? ""}
          onChange={(event) =>
            setSurveyAnswers((current) => ({
              ...current,
              [questionId]: event.target.value,
            }))
          }
          className={styles.radioGroup}
        >
          {(question.opcoes || []).map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      );
    }

    if (question.tipo === "lista_personalizada") {
      return renderSelectQuestion(question, question.opcoes || []);
    }

    if (question.tipo === "tabela") {
      return renderSelectQuestion(question, surveyOptions[questionId] || []);
    }

    return <Alert severity="info">Tipo de pergunta ainda nao suportado nesta tela.</Alert>;
  };

  const renderPreviewPage = (page: CertificatePage, sideLabel: string) => {
    if (!emissionData) {
      return null;
    }

    const variableMap = buildCertificateVariableMap(
      emissionData.dados,
      emissionData.codigo,
      formatarDatetimeToDataBr(emissionData.impresso_em),
    );
    const orientation = emissionData.certificate_template.page.orientation;
    const { width, height } = getPdfPageSize(orientation);

    return (
      <div className={styles.previewPage}>
        <div
          className={styles.previewCanvas}
          style={{
            width,
            height,
            backgroundImage: page.background?.dataUrl ? `url(${page.background.dataUrl})` : "none",
            backgroundSize:
              page.background?.fit === "stretch"
                ? "100% 100%"
                : page.background?.fit || "contain",
          }}
        >
          {page.elements.map((element) => {
            const baseStyle = {
              left: element.position.x || 0,
              top: element.position.y || 0,
              width: element.size.width,
              height: element.size.height,
            };

            if (element.type === "html_text") {
              return (
                <div
                  key={element.id}
                  className={`${styles.previewElement} ${styles.previewText}`}
                  style={{
                    ...baseStyle,
                    fontFamily: element.style.fontFamily,
                    fontSize: `${element.style.fontSize}px`,
                    color: element.style.color,
                    textAlign: element.style.align,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: replaceCertificateVariablesInHtml(element.contentHtml, variableMap),
                  }}
                />
              );
            }

            if (element.type === "image") {
              return (
                <div key={element.id} className={styles.previewElement} style={baseStyle}>
                  <img src={element.dataUrl} alt={element.label} className={styles.previewImage} />
                </div>
              );
            }

            const qrImageSize = element.qrSize || Math.min(element.size.width || 120, 120);
            const qrText = (
              <div
                className={styles.previewQrText}
                style={{
                  fontFamily: element.style.fontFamily,
                  fontSize: `${Math.max(element.style.fontSize * 0.16, 7)}px`,
                  color: element.style.color,
                }}
              >
                {element.institutionText ? (
                  <p>{replaceCertificateVariables(element.institutionText, variableMap)}</p>
                ) : null}
                <p>
                  Chave de validação:{" "}
                  <strong>{replaceCertificateVariables(element.validationKey, variableMap)}</strong>. Data de
                  emissão:{" "}
                  <strong>{replaceCertificateVariables(element.issueDate, variableMap)}</strong>
                </p>
                <p>{replaceCertificateVariables(element.instructionText, variableMap)}</p>
              </div>
            );

            return (
              <div key={element.id} className={styles.previewElement} style={baseStyle}>
                <div
                  className={
                    element.textPosition === "bottom"
                      ? styles.previewQrWrapBottom
                      : styles.previewQrWrap
                  }
                >
                  <img
                    src={element.qrCodeDataUrl}
                    alt="QR Code"
                    className={styles.previewQrImage}
                    style={{
                      width: `${qrImageSize}px`,
                    }}
                  />
                  {qrText}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ padding: "10px 16px", color: "#6b7280", fontWeight: 500 }}>{sideLabel}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.loadingArea}>
        <CircularProgress />
        <span>Carregando documento disponível...</span>
      </div>
    );
  }

  if (!detail) {
    return <Alert severity="error">{msgError || "Documento nao encontrado."}</Alert>;
  }

  return (
    <div className={styles.detailLayout}>
      <div className={styles.topActions}>
        <Button
          variant="contained"
          className={stylesButton.buttonSquareSecondary}
          startIcon={<ArrowBackOutlined />}
          onClick={() => navigate("/certificados-declaracoes")}
        >
          Voltar
        </Button>
      </div>

      {msgError && <Alert severity="error">{msgError}</Alert>}
      {msgSuccess && <Alert severity="success">{msgSuccess}</Alert>}

      <div className={styles.summaryCard}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <h3>{detail.acao_nome}</h3>
            <p>{detail.turma_nome}</p>
          </div>
          <div className={styles.badgeRow}>
            <span
              className={
                inferDocumentTypeLabel(detail) === "Declaração" ? styles.badgeOutline : styles.badge
              }
            >
              {inferDocumentTypeLabel(detail)}
            </span>
            {detail.pesquisa?.configurada ? (
              detail.pesquisa.pendente ? (
                <span className={styles.badgeWarning}>
                  {detail.pesquisa.obrigatoria_para_certificado
                    ? "Pesquisa obrigatória pendente"
                    : "Pesquisa opcional pendente"}
                </span>
              ) : (
                <span className={styles.badgeSuccess}>Pesquisa preenchida</span>
              )
            ) : (
              <span className={styles.badgeMuted}>Sem pesquisa</span>
            )}
          </div>
        </div>

        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <CalendarMonthOutlined fontSize="small" />
            <span>
              <strong>disponível em:</strong> {formatarDataDbParaBr(detail.data_inicio_disponibilizacao)}
            </span>
          </div>
          <div className={styles.infoItem}>
            <ScheduleOutlined fontSize="small" />
            <span>
              <strong>Até:</strong>{" "}
              {detail.data_fim_disponibilizacao
                ? formatarDataDbParaBr(detail.data_fim_disponibilizacao)
                : "Sem prazo final"}
            </span>
          </div>
          {addressLabel ? (
            <div className={styles.infoItem}>
              <LocationOnOutlined fontSize="small" />
              <span>
                <strong>Local:</strong> {addressLabel}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {(surveyIsBlocking || surveyIsOptionalPending) && surveyTemplate && (
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>Pesquisa de satisfação</h2>

          {surveyIsBlocking ? (
            <Alert severity="warning">
              Esta pesquisa é obrigatória e precisa ser preenchida antes da emissão do documento.
            </Alert>
          ) : (
            <Alert severity="info">
              A pesquisa estÃ¡ disponível para preenchimento, mas nÃ£o bloqueia a emissão do documento.
            </Alert>
          )}

          <div>
            <strong>{surveyTemplate.titulo}</strong>
            {surveyTemplate.descricao ? <div>{surveyTemplate.descricao}</div> : null}
            {surveyTemplate.mensagem ? <div style={{ marginTop: 8 }}>{surveyTemplate.mensagem}</div> : null}
          </div>

          <div className={styles.surveyQuestionList}>
            {activeSurveyQuestions.map((question) => (
              <div key={question.id} className={styles.surveyQuestionCard}>
                <div className={styles.surveyQuestionHeader}>
                  <h3 className={styles.surveyQuestionTitle}>
                    {question.ordem}. {question.nome}
                  </h3>
                  {question.obrigatorio ? (
                    <span className={styles.requiredBadge}>obrigatória</span>
                  ) : null}
                </div>
                {renderQuestionField(question)}
              </div>
            ))}
          </div>

          <div className={styles.cardActions}>
            <Button
              variant="contained"
              className={`${stylesButton.buttonSquarePrimary} ${loadingSurvey ? stylesButton.buttonLoad : ""}`}
              onClick={handleSubmitSurvey}
              disabled={loadingSurvey}
            >
              {loadingSurvey ? "Enviando..." : "Enviar respostas"}
            </Button>
          </div>
        </div>
      )}

      <div className={styles.sectionCard}>
        <h2 className={styles.sectionTitle}>Emissão do documento</h2>

        {surveyIsBlocking ? (
          <Alert severity="warning">
            A pré-visualização será liberada após o preenchimento da pesquisa obrigatória.
          </Alert>
        ) : (
          <Alert severity="info">
            Você pode gerar a pré-visualização para conferência ou baixar o PDF diretamente.
          </Alert>
        )}

        <div className={styles.cardActions}>
          <Button
            variant="contained"
            className={`${stylesButton.buttonSquarePrimary} ${loadingEmit ? stylesButton.buttonLoad : ""}`}
            startIcon={<PreviewOutlined />}
            onClick={handleEmitCertificate}
            disabled={loadingEmit || surveyIsBlocking}
          >
            {loadingEmit ? "Gerando..." : emissionData ? "Atualizar pré-visualização" : "Gerar pré-visualização"}
          </Button>

          <Button
            variant="outlined"
            className={stylesButton.buttonSquarePrimary}
            startIcon={<DownloadOutlined />}
            onClick={handleDownloadPdf}
            disabled={loadingDownload || surveyIsBlocking}
          >
            {loadingDownload ? "Baixando..." : "Baixar PDF"}
          </Button>
        </div>

        {previewPages ? (
          <div className={styles.previewStack}>
            {renderPreviewPage(previewPages.front, "Frente")}
            {previewPages.back?.enabled ? renderPreviewPage(previewPages.back, "Verso") : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EmitirCertificado;

